import { Box, Flex, Skeleton, Text } from '@radix-ui/themes'
import { IconInfoCircle } from '@tabler/icons-react'
import clsx from 'clsx'
import { getMarksStyle } from '@/utils/style'
import Hstack from '../hstack'
import Stack from '../stack'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip'

export type StatBoxProps = {
  icon: React.ReactNode
  title: string
  value: string | number
  className?: string
  isLoading?: boolean
  valueSuffix?: string
  children?: React.ReactNode
  showTooltip?: boolean
  totalMark?: number
}
export default function StatBox({
  icon,
  title,
  value,
  isLoading = false,
  children,
  className,
  valueSuffix,
  showTooltip = false,
  totalMark,
}: StatBoxProps) {
  const textClass =
    [
      'Average Practice Test Score',
      'Average Mock Exam Score',
      'Average Accuracy',
      'Average AI Marks',
      'Average Tutor Marks',
      'Average AI Mark',
      'Average Tutor Mark',
      'Average Practice Test Score',
      'Average Mock Exam Score',
    ].includes(title) && value !== 'N/A'
      ? getMarksStyle(Number(value), totalMark)
      : 'text-[#002652]'

  return (
    <Box
      height={'fit-full'}
      width={'full'}
      p={'20px'}
      className={clsx(className, 'rounded-[12px] bg-white')}
    >
      <Hstack height={'fit-content'} gap={'20px'}>
        <>{icon}</>
        <Stack justify={'between'} gap={'12px'} className='overflow-hidden'>
          <Flex align={'center'} gap={'1'}>
            <Text className='truncate text-[#002652]'>{title ?? ''}</Text>
            {showTooltip && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className='min-h-6 min-w-6' />
                  </TooltipTrigger>
                  <TooltipContent
                    hideArrow
                    className='max-w-[400px] bg-[#D9D9D9] text-xs text-[#000000]'
                  >
                    <p>The colors show how well your class performed.</p>
                    <ul className='ml-1 list-inside list-disc'>
                      <li>
                        <span className='font-bold text-[#D92D20]'>Red</span>{' '}
                        indicates a score{' '}
                        <span className='font-bold text-[#D92D20]'>
                          below 45%
                        </span>
                        , which is a{' '}
                        <span className='font-bold text-[#D92D20]'>Fail</span>.
                      </li>
                      <li>
                        <span className='font-bold text-[#F79009]'>Amber</span>{' '}
                        (yellow) means the score is{' '}
                        <span className='font-bold text-[#F79009]'>
                          between 45% and 52%
                        </span>
                        , which is a{' '}
                        <span className='font-bold text-[#F79009]'>
                          Borderline Pass
                        </span>
                        .
                      </li>
                      <li>
                        <span className='font-bold text-[#079455]'>Green</span>{' '}
                        means the score is{' '}
                        <span className='font-bold text-[#079455]'>
                          above 52%
                        </span>
                        , which is a{' '}
                        <span className='font-bold text-[#079455]'>
                          Safe Pass
                        </span>
                        .
                      </li>
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Flex>
          <Skeleton loading={isLoading} className='h-full'>
            <Text
              className={clsx(
                'text-[60px] !leading-none font-normal',
                textClass
              )}
            >
              {value}
              <span className='text-[32px]'>{valueSuffix}</span>
              {!!totalMark && value !== 'N/A' && (
                // <span className='text-[30px] text-[#002652]'>/{totalMark}</span>
                <span className={`text-[30px] ${textClass}`}>%</span>
              )}
            </Text>
          </Skeleton>
        </Stack>
        <Box>{children}</Box>
      </Hstack>
    </Box>
  )
}
